'use client';

import { FC, useEffect } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Grid } from '@purpur/library';

import { Carousel } from '@telialt/common-carousel/src';
import { useInitializeTranslations } from '@telialt/common-cms/src/useTranslations';
import { ConnectedProductCard } from '@telialt/core-search/src/Common/ConnectedProductCard/ConnectedProductCard';
import { useLoadProductsCustomQuery } from '@telialt/core-search/src/hooks/useLoadProductsCustomQuery';
import { useLoadQueryKeys } from '@telialt/core-search/src/hooks/useLoadQueryKeys';
import { ProductSearchItem } from '@telialt/core-search/src/model';
import { trackSelectItemGA, trackViewItemListGA } from '@telialt/gtm-tracking/src/events';
import { useSegment } from '@telialt/utils/src/segment';

import { BlockProductsType } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import { BlockHeader } from '../common/BlockHeader';
import { BlockProductsSection, BlockProductsSlide } from './BlockProducts.styles';
import { getDefaultProductsQueryKey } from './getDefaultProductsQueryKey';
import { transformBlockProducts } from './transformers';

type Props = {
    contentItem: BlockProductsType;
    gridContainerClassName?: string;
};

export const BlockProducts: FC<Props> = ({ contentItem: initialContentItem, gridContainerClassName }) => {
    const { t } = useInitializeTranslations();

    const contentItem = useContentfulLiveUpdates(initialContentItem);

    const data = transformBlockProducts(contentItem);
    const segment = useSegment();
    const needsDefaultQueryKey = !data?.queryKey && !!data?.searchFilters;
    const { queryKeys, isLoading: isQueryKeysLoading } = useLoadQueryKeys(needsDefaultQueryKey);
    const queryKey =
        data?.queryKey ?? (needsDefaultQueryKey ? getDefaultProductsQueryKey(segment, queryKeys) : undefined);

    const { data: products, isLoading } = useLoadProductsCustomQuery({
        queryKey,
        filters: data?.searchFilters,
        sort: data?.sort,
        limit: data?.limit,
    });

    useEffect(() => {
        if (!queryKey) {
            return;
        }

        trackViewItemListGA({
            source: 'searchProducts',
            data: {
                products: products?.items || [],
                itemListName: data.blockTitle || '',
                itemCategory: data.blockTitle || '',
            },
        });
    }, [data.blockTitle, products?.items, queryKey]);

    if (!t || !queryKey || isQueryKeysLoading || isLoading || !products?.items?.length) {
        return null;
    }

    const handleProductClick = (product: ProductSearchItem) => {
        trackSelectItemGA({
            source: 'searchProducts',
            data: {
                products: [product],
                itemListName: data.blockTitle || '',
                itemCategory: data.blockTitle || '',
            },
        });
    };

    const slides = products.items.map((product, index) => (
        <BlockProductsSlide key={product.id} data-section="Carousel Card" data-section-index={index + 1}>
            <ConnectedProductCard product={product} headingTag="h3" onClick={() => handleProductClick(product)} />
        </BlockProductsSlide>
    ));

    return (
        <BlockProductsSection
            data-section="Product Carousel"
            data-section-name={data.blockTitle}
            id={getBlockId(contentItem)}
        >
            <Grid noGap={true} containerClassName={gridContainerClassName}>
                <BlockHeader title={data.blockTitle} cta={data.blockCta} />
                <Carousel variant="dynamic-width" slides={slides} noControlsMargin={true} />
            </Grid>
        </BlockProductsSection>
    );
};
